import { inject, Injectable } from '@angular/core';
import {
  type ApplicationEnum,
  type CreateLearningResourceGlobalCategoryInput,
  type CreateLearningResourceGlobalCategoryMutationMutation,
  type CreateLearningResourceGlobalCategoryMutationMutationVariables,
  type DestroyLearningResourceGlobalCategoryInput,
  type DestroyLearningResourceGlobalCategoryMutation,
  type DestroyLearningResourceGlobalCategoryMutationVariables,
  LanguageEnum,
  type LearningResourceGlobalCategoriesQuery,
  type LearningResourceGlobalCategoriesQueryVariables,
  type LearningResourceGlobalCategoriesWithSetIdsQuery,
  type LearningResourceGlobalCategoriesWithSetIdsQueryVariables,
  type LearningResourceGlobalCategoryByIdQuery,
  type LearningResourceGlobalCategoryByIdQueryVariables,
  type LearningResourceGlobalCategoryWithItemsQuery,
  type LearningResourceGlobalCategoryWithItemsQueryVariables,
  type LearningResourceGlobalCategoryWithLearningSetsByIdQuery,
  type LearningResourceGlobalCategoryWithLearningSetsByIdQueryVariables,
  type UpdateLearningResourceGlobalCategoryInput,
  type UpdateLearningResourceGlobalCategoryMutation,
  type UpdateLearningResourceGlobalCategoryMutationVariables,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { map } from 'rxjs/operators';
import { type BatchMutationVariables } from '../../../services/graphql.service';
import { SharedGraphQlService } from '../../../services/shared-graphql.service';
import { assertGraphqlDataNode } from '../../../utils/type-helpers';
import { type LearningResourceGlobalCategoryId } from '../../../utils/type-helpers-ids.generated';
import { learningResourceSetWithItemsFragment } from '../learning-resource-set-with-items.fragment';

const learningResourceGlobalCategoryMinimal = gql`
  fragment LearningResourceGlobalCategoryMinimal on LearningResourceGlobalCategory {
    id
    title
    description
    shortName
    language
    application
    order
  }
`;

const rootLearningResourceGlobalCategories = gql`
  query LearningResourceGlobalCategories($application: ApplicationEnum!, $language: LanguageEnum) {
    learningResourceGlobalCategories(application: $application, language: $language) {
      nodes {
        ...LearningResourceGlobalCategoryMinimal
      }
    }
  }
  ${learningResourceGlobalCategoryMinimal}
`;

export const batchUpdateLearningResourceGlobalCategoryItem = `
  updateLearningResourceGlobalCategory(input: $learningResourceGlobalCategoryInput) {

    clientMutationId

    learningResourceGlobalCategory {
      id
      order
    }

    errors {
      message
      path
    }

  }
`;

@Injectable({ providedIn: 'root' })
export class LearningResourceGlobalCategoryApi {
  private readonly sharedGraphQlService = inject(SharedGraphQlService);

  watchLearningResourceGlobalCategoriesForApplication(application: ApplicationEnum) {
    return this.sharedGraphQlService
      .watchQuery<LearningResourceGlobalCategoriesQuery, LearningResourceGlobalCategoriesQueryVariables>({
        query: rootLearningResourceGlobalCategories,
        variables: {
          application,
        },
      })
      .pipe(map((res) => res.data.learningResourceGlobalCategories));
  }

  watchLearningResourceGlobalCategoriesForApplicationAndLanguages(
    application: ApplicationEnum,
    language?: `${LanguageEnum}`,
  ) {
    return this.sharedGraphQlService
      .watchQuery<
        LearningResourceGlobalCategoriesWithSetIdsQuery,
        LearningResourceGlobalCategoriesWithSetIdsQueryVariables
      >({
        query: gql`
          query LearningResourceGlobalCategoriesWithSetIds($application: ApplicationEnum!, $language: LanguageEnum) {
            learningResourceGlobalCategories(application: $application, language: $language) {
              nodes {
                ...LearningResourceGlobalCategoryMinimal
              }
            }
          }
          ${learningResourceGlobalCategoryMinimal}
        `,
        variables: {
          application,
          // @ts-expect-error TS2538 invalid index type
          language: LanguageEnum[language],
        },
      })
      .pipe(map((res) => res.data.learningResourceGlobalCategories));
  }

  watchLearningResourceGlobalCategory(globalLearningResourceId: LearningResourceGlobalCategoryId) {
    return this.sharedGraphQlService
      .watchQuery<LearningResourceGlobalCategoryByIdQuery, LearningResourceGlobalCategoryByIdQueryVariables>({
        query: gql`
          query LearningResourceGlobalCategoryById($id: ID!) {
            node(id: $id) {
              ... on LearningResourceGlobalCategory {
                ...LearningResourceGlobalCategoryMinimal
              }
            }
          }
          ${learningResourceGlobalCategoryMinimal}
        `,
        variables: { id: globalLearningResourceId.id },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'LearningResourceGlobalCategory')));
  }

  watchLearningResourceGlobalCategoryWithItems(globalLearningResourceId: LearningResourceGlobalCategoryId) {
    return this.sharedGraphQlService
      .watchQuery<LearningResourceGlobalCategoryWithItemsQuery, LearningResourceGlobalCategoryWithItemsQueryVariables>({
        query: gql`
          query LearningResourceGlobalCategoryWithItems($id: ID!) {
            node(id: $id) {
              ... on LearningResourceGlobalCategory {
                ...LearningResourceGlobalCategoryMinimal
                learningResourceSets {
                  nodes {
                    ...LearningResourceSetWithItems
                  }
                }
              }
            }
          }
          ${learningResourceGlobalCategoryMinimal}
          ${learningResourceSetWithItemsFragment}
        `,
        variables: { id: globalLearningResourceId.id },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'LearningResourceGlobalCategory')));
  }

  watchLearningResourceGlobalWithLearningSets(globalLearningResourceId: LearningResourceGlobalCategoryId) {
    return this.sharedGraphQlService
      .watchQuery<
        LearningResourceGlobalCategoryWithLearningSetsByIdQuery,
        LearningResourceGlobalCategoryWithLearningSetsByIdQueryVariables
      >({
        query: gql`
          query LearningResourceGlobalCategoryWithLearningSetsById($id: ID!) {
            node(id: $id) {
              ... on LearningResourceGlobalCategory {
                ...LearningResourceGlobalCategoryMinimal

                learningResourceSets {
                  nodes {
                    id
                    order
                  }
                }
              }
            }
          }
          ${learningResourceGlobalCategoryMinimal}
        `,
        variables: { id: globalLearningResourceId.id },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'LearningResourceGlobalCategory')));
  }

  destroyLearningResourceGlobalCategory(input: DestroyLearningResourceGlobalCategoryInput) {
    return this.sharedGraphQlService.mutateV2<
      DestroyLearningResourceGlobalCategoryMutation,
      DestroyLearningResourceGlobalCategoryMutationVariables
    >({
      mutation: gql`
        mutation destroyLearningResourceGlobalCategory($input: DestroyLearningResourceGlobalCategoryInput!) {
          destroyLearningResourceGlobalCategory(input: $input) {
            errors {
              message
              path
            }
          }
        }
      `,
      variables: { input },
      refetchQueries: [
        {
          query: rootLearningResourceGlobalCategories,
        },
      ],
    });
  }

  updateLearningResourceGlobalOrder(learningResourceGlobalCategories: { id: string; order: number }[]) {
    const toUpdate = learningResourceGlobalCategories.map((lr, index) => ({
      learningResourceGlobalCategoryId: lr.id,
      order: index,
    }));
    const mutations: string[] = [];
    const variables: { [variableKey: string]: BatchMutationVariables }[] = [];

    toUpdate.forEach((learningResourceGlobalCategoryInput) => {
      mutations.push(batchUpdateLearningResourceGlobalCategoryItem);
      variables.push({
        learningResourceGlobalCategoryInput: {
          type: 'UpdateLearningResourceGlobalCategoryInput!',
          value: learningResourceGlobalCategoryInput,
        },
      });
    });

    return this.sharedGraphQlService.mutateBatch({
      mutations,
      variables,
    });
  }

  updateLearningGlobalCategory(input: UpdateLearningResourceGlobalCategoryInput) {
    return (
      this.sharedGraphQlService
        .mutateV2<UpdateLearningResourceGlobalCategoryMutation, UpdateLearningResourceGlobalCategoryMutationVariables>({
          mutation: gql`
            mutation updateLearningResourceGlobalCategory($input: UpdateLearningResourceGlobalCategoryInput!) {
              updateLearningResourceGlobalCategory(input: $input) {
                learningResourceGlobalCategory {
                  ...LearningResourceGlobalCategoryMinimal
                }
                errors {
                  message
                  path
                }
              }
            }
            ${learningResourceGlobalCategoryMinimal}
          `,
          variables: { input },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.updateLearningResourceGlobalCategory.learningResourceGlobalCategory))
    );
  }

  createLearningResourceGlobalCategory(input: CreateLearningResourceGlobalCategoryInput) {
    return (
      this.sharedGraphQlService
        .mutateV2<
          CreateLearningResourceGlobalCategoryMutationMutation,
          CreateLearningResourceGlobalCategoryMutationMutationVariables
        >({
          mutation: gql`
            mutation CreateLearningResourceGlobalCategoryMutation($input: CreateLearningResourceGlobalCategoryInput!) {
              createLearningResourceGlobalCategory(input: $input) {
                errors {
                  message
                  path
                }
                learningResourceGlobalCategory {
                  ...LearningResourceGlobalCategoryMinimal
                }
              }
            }
            ${learningResourceGlobalCategoryMinimal}
          `,
          variables: { input },
          refetchQueries: [
            {
              query: rootLearningResourceGlobalCategories,
              variables: {
                application: input.application,
              },
            },
          ],
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.createLearningResourceGlobalCategory.learningResourceGlobalCategory))
    );
  }
}
